import { Spinner } from "@blueprintjs/core";
import { FGDateMaskInput, FGEmpty, FGNumberInput, FieldGroup, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { BroodRequestApi } from "../../../../api";
import assets from "../../../../assets";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { generateAddress } from "../../../../utils";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { DrupalParentInfoBox } from "./DrupalParentInfoBox";
import { DrupalPotentialFathersList } from "./DrupalPotentialFathersList";
import { DrupalPuppyInfoBox } from "./DrupalPuppyInfoBox";
import { FGSRSHDateMaskInput } from "../../../../components";

export interface IDrupalBabyDogDetailProps {}

const StyledMainContainer = styled.div`
  margin: 2rem;
  background-color: #d6d9f3;
  padding-left: 2rem;
  padding-bottom: 1rem;
  padding-right: 2rem;
`;

const StyledSpacing = styled.div`
  margin: 1rem;
`;

const StyledParentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`;

const StyledImage = styled.img`
  margin-top: 2rem !important;
  margin-bottom: 2rem !important;
  width: 3rem;
  height: 3rem;
  margin: auto;
  display: block;
`;

const StyledIcon = styled.i`
  margin-right: 0.5rem;
  color: #5f6069;
`;

const StyledLine = styled.div`
  display: flex;
`;

export const DrupalBabyDogDetail: React.FunctionComponent<IDrupalBabyDogDetailProps> = () => {
  const { t } = useTl();
  const { id } = useParams<{ id: string }>();
  const broodRequestApi = useApiService(BroodRequestApi);

  const [data, loading] = useApiEffect(() => broodRequestApi.getDrupal(+id));

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <DrupalFormGenerator
          initialValues={data}
          onSubmit={() => {}}
          editMode={false}
          hideButtons
          labelAlignment="left"
        >
          <StyledMainContainer>
            <h1>{data?.breeder?.computedName}</h1>
            <DrupalFieldSet title={t(ETLCodes.Contact)}>
              <FieldGroup columns={2}>
                <div>
                  <StyledLine>
                    <StyledIcon className="fas fa-home" />
                    <p>
                      {generateAddress(
                        data?.breeder?.number,
                        data?.breeder?.street,
                        data?.breeder?.city,
                        data?.breeder?.postalCode,
                        data?.breeder?.countryIso
                      )}
                    </p>
                  </StyledLine>
                  <StyledLine>
                    <StyledIcon className="fas fa-phone" />
                    <p>{data?.breeder?.phoneNumber}</p>
                    <FGEmpty />
                    <p>{data?.breeder?.mobilePhoneNumber}</p>
                  </StyledLine>
                  <StyledLine>
                    <StyledIcon className="far fa-envelope" />
                    <p>{data?.breeder?.email}</p>
                  </StyledLine>
                  <StyledLine>
                    <StyledIcon className="fas fa-globe-americas" />
                    <p>{data?.breeder?.email}</p>
                  </StyledLine>
                  {data?.breeder?.hkNumber && (
                    <StyledLine>
                      <StyledIcon className="fas fa-paw" />
                      <p>{data?.breeder?.hkNumber}</p>
                    </StyledLine>
                  )}
                </div>
                <FieldGroup>
                  <FGSRSHDateMaskInput label={t(ETLCodes.BirthDate)} name="birthdate" />
                  <FGNumberInput label={t(ETLCodes.NumberOfPuppies)} name="numberOfPuppies" />
                  <StyledLine>
                    <FGNumberInput label={t(ETLCodes.Price)} name="price" />
                    <StyledIcon className="fas fa-euro-sign" />
                  </StyledLine>
                </FieldGroup>
              </FieldGroup>
            </DrupalFieldSet>
          </StyledMainContainer>
          {/*--------------------------------------------------------------------------------------------------------------------------------------------------*/}
          <StyledSpacing />
          <DrupalFieldSet title={t(ETLCodes.Parents)}>
            <StyledParentContainer>
              <DrupalParentInfoBox
                name={data?.mother?.dogName}
                chipNumberFG="mother.chipNumber"
                pedigreeFG="mother.pedigree"
                breedFG="mother.breedId"
                colorFG="mother.colorId"
                sex={data?.mother?.sex}
              />
              <StyledImage src={assets.images.cross} alt="cross" />
            </StyledParentContainer>
            <DrupalPotentialFathersList broodRequestId={+id} />
          </DrupalFieldSet>
          {/*--------------------------------------------------------------------------------------------------------------------------------------------------*/}
          <DrupalFieldSet title={t(ETLCodes.Puppies)}>
            {data?.puppies?.map((puppy, index) => (
              <DrupalPuppyInfoBox
                key={index}
                name={data?.puppies[index]?.name}
                chipNumberFG={`puppies[${index}].chipNumber`}
                pedigreeFG={`puppies[${index}].pedigreeNumber`}
                breedFG={`puppies[${index}].breedId`}
                colorFG={`puppies[${index}].colorId`}
                sex={data?.puppies[index]?.sex}
                index={index}
                availabilityFG={`puppies[${index}].available`}
              />
            ))}
          </DrupalFieldSet>
        </DrupalFormGenerator>
      )}
    </>
  );
};
